import { useState, useEffect, useRef } from "react";
import "intl-tel-input/build/css/intlTelInput.css";
import { lazy, Suspense } from "react";
const ReCAPTCHA = lazy(() => import("react-google-recaptcha"));
import { Helmet } from "@dr.pogodin/react-helmet";

function EnquiryForm() {
    const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    contactNumber: "",
    service: "",
    message: "",
    captchaToken: "",
  });

  const [errors, setErrors] = useState({});
  const [utilsLoaded, setUtilsLoaded] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const phoneInputRef = useRef(null);
  const itiRef = useRef(null);
  const recaptchaRef = useRef(null);

  const sanitizeInput = (str) =>
    str
      .replace(/</g, "")
      .replace(/>/g, "")
      .replace(/[\$]/g, "")
      .replace(/[{}]/g, "")
      .replace(/["']/g, "")
      .replace(/;/g, "")
      .replace(/\\/g, "")
      .trim();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!phoneInputRef.current) return;

    const inputEl = phoneInputRef.current;

    import("intl-tel-input").then(({ default: intlTelInput }) => {
    itiRef.current = intlTelInput(inputEl, {
      initialCountry: "in",
      nationalMode: false,
      formatOnDisplay: true,
      separateDialCode: true,
      utilsScript:
        "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.12/js/utils.js",
    });
    });

    const checkUtilsLoaded = setInterval(() => {
      if (
        window.intlTelInputUtils &&
        itiRef.current &&
        typeof itiRef.current.isValidNumber === "function"
      ) {
        setUtilsLoaded(true);
        clearInterval(checkUtilsLoaded);
      }
    }, 300);

    const handleCountryChange = () => {
      const selected = itiRef.current.getSelectedCountryData();
      setFormData((prev) => ({
        ...prev,
        countryCode: `+${selected.dialCode}`,
      }));
    };

    const handlePhoneInput = () => {
      const selected = itiRef.current.getSelectedCountryData();
      const number = itiRef.current.getNumber();
      setFormData((prev) => ({
        ...prev,
        contactNumber: number,
        countryCode: `+${selected.dialCode}`,
      }));
      setErrors((prev) => ({ ...prev, contactNumber: "" }));
    };

    inputEl.addEventListener("countrychange", handleCountryChange);
    inputEl.addEventListener("input", handlePhoneInput);

    return () => {
      inputEl.removeEventListener("countrychange", handleCountryChange);
      inputEl.removeEventListener("input", handlePhoneInput);
      clearInterval(checkUtilsLoaded);
      itiRef.current?.destroy();
    };
  }, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "contactNumber") return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNumericInput = (e) => {
    const original = e.target.value;
    const digitsOnly = original.replace(/\D/g, "");
    if (original !== digitsOnly) e.target.value = digitsOnly;
    setFormData((prev) => ({ ...prev, contactNumber: digitsOnly }));
    setErrors((prev) => ({ ...prev, contactNumber: "" }));
  };

  const validate = (data, phoneValue, isValid) => {
    const newErrors = {};

    if (!data.name.trim()) newErrors.name = "Full name is required.";

    if (!data.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (
      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(data.email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!phoneValue.trim()) {
      newErrors.contactNumber = "Contact number is required.";
    } else if (!isValid) {
      newErrors.contactNumber = "Please enter a valid phone number.";
    }

    if (!data.service) {
        newErrors.service = "Please select a service.";
    }

    if (!data.captchaToken) {
      newErrors.captcha = "Please verify that you are not a robot.";
    }

    const suspiciousPattern =
      /\b(select|insert|delete|update|drop|truncate|script|iframe|alert|eval|union|--|#|;)\b/i;
    if (!data.message.trim()) {
      newErrors.message = "Message cannot be empty.";
    } else if (suspiciousPattern.test(data.message)) {
      newErrors.message = "Suspicious content detected in your message.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage("");
    setStatusType("");
    setIsSubmitting(true);

    if (!utilsLoaded || !window.intlTelInputUtils) {
      setStatusMessage("Please wait for phone validation to load fully.");
      setStatusType("error");
      setIsSubmitting(false);
      return;
    }

    let isValid = false;
    let validatedNumber = "";
    let countryCode = formData.countryCode;
    let phoneValue = phoneInputRef.current?.value?.trim() || "";
    let localNumber = phoneValue;

    if (itiRef.current) {
      const selected = itiRef.current.getSelectedCountryData();
      const dialCode = selected?.dialCode ? `+${selected.dialCode}` : "";
      const fullNumber = phoneValue.startsWith("+")
        ? phoneValue
        : `${dialCode}${phoneValue}`;

      try {
        validatedNumber = itiRef.current.getNumber(
          window.intlTelInputUtils.numberFormat.E164
        );
        if (!validatedNumber) validatedNumber = fullNumber;

        isValid = window.intlTelInputUtils.isValidNumber(
          validatedNumber,
          selected.iso2
        );

        if (selected.iso2 === "in") {
          const local = phoneValue.replace(/\D/g, "");
          if (local.length !== 10 || !/^[6-9]\d{9}$/.test(local)) {
            isValid = false;
            console.warn(
              "Invalid Indian number — must be 10 digits and start with 6-9"
            );
          }
        }

        countryCode = dialCode;
        localNumber = validatedNumber.replace(dialCode, "").trim();
      } catch (err) {
        console.error("Validation error:", err);
        validatedNumber = fullNumber;
      }
    }

    if (!validate(formData, phoneValue, isValid)) {
        setIsSubmitting(false);
        return;
      }

    const safeData = {
        enqName: sanitizeInput(formData.name),
        enqEmail: sanitizeInput(formData.email),
        enqCountryCode: countryCode,
        enqContactNumber: sanitizeInput(localNumber),
        enqService: formData.service,
        enqMessage: sanitizeInput(formData.message),
        captchaToken: formData.captchaToken,
    };

    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_API_URL}/submit-enquiry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(safeData),
        }
      );

      if (response.ok) {
        setStatusMessage("Message sent successfully!");
        setStatusType("success");
        setFormData({
          name: "",
          email: "",
          countryCode: "+91",
          contactNumber: "",
          service: "",
          message: "",
          captchaToken: "",
        });
        setErrors({});
        itiRef.current?.setNumber("");

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        setTimeout(() => {
          setStatusMessage("");
          setStatusType("");
        }, 3000);

      } else {
        setStatusMessage("Failed to send message. Please try again later.");
        setStatusType("error");
      }
    } catch (error) {
      console.error("Network error:", error);
      setStatusMessage("Error occurred. Please check your connection.");
      setStatusType("error");
    } finally {
      setIsSubmitting(false);
    }
  };
   return (
      <>
        <div className="card enqury-form-card">
          <div className="card-body p-0">
            <h5 className="mb-5 form-main-title">Let’s connect</h5>

            {statusMessage && (
                <div
                className={`alert ${
                    statusType === "success" ? "alert-success" : "alert-danger"
                }`}
                >
                {statusMessage}
                </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="row mb-4">
                <div className="col-lg-6">
                    <input className="form-control" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
                    {errors.name && <div className="error-msg mt-1">{errors.name}</div>}
                </div>
                <div className="col-lg-6 mt-lg-0 mt-4">
                    <input className="form-control" type="text" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" />
                    {errors.email && <div className="error-msg mt-1">{errors.email}</div>}
                </div>
                </div>

                <div className="mb-4">
                <input ref={phoneInputRef} className="form-control" type="tel" id="contactNumber" name="contactNumber" placeholder="Phone Number" onInput={handleNumericInput} inputMode="numeric" pattern="[0-9]*" />
                {errors.contactNumber && (
                    <div className="error-msg mt-1">{errors.contactNumber}</div>
                )}
                </div>

                  <div className="mb-4">
                    <select name="service" className="form-select" value={formData.service} onChange={handleInputChange} >
                    <option value="">Select Service</option>
                    <option value="web development">Web Development</option>
                    <option value="app development">App Development</option>
                    <option value="digital marketing">Digital Marketing</option>
                    <option value="ai chatbot">AI Chatbot</option>
                    <option value="rag">RAG</option>
                    <option value="ai software services">AI Software Services</option>
                    </select>
                    {errors.service && <div className="error-msg mt-1">{errors.service}</div>}
                </div>

                <div className="mb-4">
                <textarea className="form-control" name="message" rows="4" value={formData.message} onChange={handleInputChange} placeholder="Type your message here..." ></textarea>
                {errors.message && (
                    <div className="error-msg mt-1">{errors.message}</div>
                )}
                </div>

                <div className="text-center mb-3">
                {typeof window !== "undefined" && (
                    <Suspense fallback={<div>Loading captcha...</div>}>
                    <ReCAPTCHA ref={recaptchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setFormData((prev) => ({ ...prev, captchaToken: token })) }
                    />
                    </Suspense>
                )}
                {errors.captcha && <div className="error-msg mt-1">{errors.captcha}</div>}
                </div>

                <div className="send-msg-btn">
                <button className="btn btn-primary" type="submit" disabled={!utilsLoaded || isSubmitting}>
                    {isSubmitting ? (
                    <>
                        Sending... <i className="fa fa-spinner fa-spin"></i>
                    </>
                    ) : utilsLoaded ? (
                    <>
                        Send Message
                    </>
                    ) : (
                    <>Loading phone validation...</>
                    )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
   )
}

export default EnquiryForm;